import type { SiteManifest } from './types';

/**
 * The LEARNING site manifest — the words and pictures of the member-facing
 * academy pages.
 *
 * Unlike `AGP_SITE` / `SOCIALPRENEUR_SITE` this manifest is NOT keyed by tenant
 * id: every tenant with the LMS switched on gets it, which is why it lives in
 * `SHARED_SITE_MANIFESTS` rather than `SITE_MANIFESTS` and is edited on its own
 * admin page (Learning → Learning Pages) instead of the bespoke-site
 * "Site Pages" tab. Everything downstream — the sparse `storefront.siteContent`
 * overlay, its zod guard, the `resolveStorefront` passthrough, the storefront
 * asset allow-list and `useSiteContent` — is already manifest-agnostic, so this
 * file plus one admin page is the whole mechanism.
 *
 * Defaults are the redesign's copy verbatim (`client design handoffs/
 * design_handoff_lms_redesign/`), so an un-edited tenant renders the intended
 * page.
 *
 * PRECEDENCE: eight of these fields also exist in the older typed
 * `storefront.learningHome` block (the hero's title/sub/CTA/image and the
 * share-and-earn band's title/body/CTA/href). A tenant that edited those before
 * this manifest existed must keep seeing its own copy, so the member page reads
 * overlay → `learningHome` override → manifest default. See
 * `learningHomeOverride` in `../storefront-content.ts`.
 *
 * Code-owned by design, as in every other manifest: layout, colours (the `lms*`
 * theme tokens), fonts, routes, and every NUMBER on the page (course/mentor/
 * learner counts, ratings, prices) — those come from the live catalogue, never
 * from editable copy.
 */

/** Show/hide options shared by every section toggle below. */
const SHOW_HIDE = [
  { value: 'show', label: 'Show' },
  { value: 'hide', label: 'Hide' },
];

export const LMS_SITE: SiteManifest = {
  tenant: 'lms',
  label: 'Learning pages',
  groups: [
    {
      id: 'learning',
      label: 'Courses page',
      sections: [
        {
          id: 'header',
          label: 'Header',
          hint: 'The bar across the top of every learning page. Links and the cart are not editable here.',
          fields: [
            {
              id: 'academyLabel',
              kind: 'text',
              label: 'Badge beside the logo',
              default: 'Academy',
              hint: 'Leave empty to hide the badge.',
            },
            { id: 'exploreLabel', kind: 'text', label: '"Explore" link', default: 'Explore' },
            {
              id: 'myLearningLabel',
              kind: 'text',
              label: '"My learning" link',
              default: 'My learning',
            },
            { id: 'storeLabel', kind: 'text', label: '"Store" link', default: 'Store' },
            {
              id: 'searchPlaceholder',
              kind: 'text',
              label: 'Search box placeholder',
              default: 'What do you want to learn today?',
            },
          ],
        },
        {
          id: 'hero',
          label: 'Hero',
          hint: 'The opening block. The three numbers are counted from the live catalogue — only their labels are editable.',
          fields: [
            { id: 'kicker', kind: 'text', label: 'Kicker', default: 'Learn · Sell · Earn' },
            {
              id: 'title',
              kind: 'textarea',
              label: 'Headline',
              default: 'Skills that sell,\ntaught by people',
              hint: 'Line breaks are kept. The accent phrase below is appended in brand green.',
            },
            {
              id: 'titleAccent',
              kind: 'text',
              label: 'Headline accent (green)',
              default: 'who sell.',
            },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Sub-heading',
              default:
                'Practical courses from real online sellers. Start today, learn at your own pace, and put every lesson to work.',
            },
            { id: 'ctaLabel', kind: 'text', label: 'Button', default: 'Browse all courses' },
            {
              id: 'image',
              kind: 'image',
              label: 'Photo',
              // Self-hosted, not a hotlink — see apps/member/public/learning/CREDITS.txt.
              defaultUrl: '/learning/hero.jpg',
              hint: 'Shown beside the headline. A wide photo (about 4:3) works best.',
            },
            {
              id: 'showStats',
              kind: 'select',
              label: 'Course / mentor / learner counts',
              options: SHOW_HIDE,
              default: 'show',
            },
            {
              id: 'coursesLabel',
              kind: 'text',
              label: 'Label under the course count',
              default: 'courses',
            },
            {
              id: 'mentorsLabel',
              kind: 'text',
              label: 'Label under the mentor count',
              default: 'mentors',
            },
            {
              id: 'learnersLabel',
              kind: 'text',
              label: 'Label under the learner count',
              default: 'learners',
            },
            {
              id: 'showRatingCard',
              kind: 'select',
              label: 'Average-rating card on the photo',
              options: SHOW_HIDE,
              default: 'show',
              hint: 'Also hidden whenever course ratings are switched off, or while no course has been reviewed.',
            },
            { id: 'ratingLabel', kind: 'text', label: 'Rating card caption', default: 'average' },
            {
              id: 'ratingCountLabel',
              kind: 'text',
              label: 'Rating card sub-caption',
              default: 'learner reviews',
            },
          ],
        },
        {
          id: 'spotlight',
          label: 'Featured course',
          hint: 'A single highlighted course. Which course it is comes from the "Featured" flag on the course itself; the block hides itself when no course is featured.',
          fields: [
            {
              id: 'show',
              kind: 'select',
              label: 'Show this block',
              options: SHOW_HIDE,
              default: 'show',
            },
            { id: 'label', kind: 'text', label: 'Kicker', default: 'Featured this week' },
            { id: 'enrollLabel', kind: 'text', label: 'Primary button', default: 'Enroll now' },
            {
              id: 'previewLabel',
              kind: 'text',
              label: 'Secondary button',
              default: 'Preview course',
            },
            {
              id: 'certificateChip',
              kind: 'text',
              label: 'Certificate chip',
              default: 'Certificate',
            },
          ],
        },
        {
          id: 'topics',
          label: 'Browse by topic',
          hint: 'Cards for each course topic. Topics, their pictures and their course counts are managed under Learning → Course Topics.',
          fields: [
            {
              id: 'show',
              kind: 'select',
              label: 'Show this block',
              options: SHOW_HIDE,
              default: 'show',
            },
            { id: 'heading', kind: 'text', label: 'Heading', default: 'Browse by topic' },
          ],
        },
        {
          id: 'catalog',
          label: 'All courses',
          hint: 'The filterable grid. Filter options come from the catalogue itself.',
          fields: [
            { id: 'heading', kind: 'text', label: 'Heading', default: 'All courses' },
            {
              id: 'filteredHeading',
              kind: 'text',
              label: 'Heading while filtering',
              default: 'Courses',
            },
            {
              id: 'allTopicsLabel',
              kind: 'text',
              label: '"All topics" chip',
              default: 'All topics',
            },
            {
              id: 'emptyText',
              kind: 'text',
              label: 'Message when filters match nothing',
              default: 'No courses match those filters.',
            },
            {
              id: 'emptyUnfilteredText',
              kind: 'text',
              label: 'Message when there are no courses at all',
              default: 'No courses yet — check back soon.',
            },
          ],
        },
        {
          id: 'rails',
          label: 'Free / Paid / Subscription rows',
          hint: 'Three rows under All courses, one per way to get a course. A row hides itself when the catalog has no course of that kind.',
          fields: [
            {
              id: 'show',
              kind: 'select',
              label: 'Show these rows',
              options: SHOW_HIDE,
              default: 'show',
            },
            { id: 'freeTitle', kind: 'text', label: 'Free row heading', default: 'Free courses' },
            {
              id: 'freeBody',
              kind: 'text',
              label: 'Free row sub-heading',
              default: 'Start straight away. No payment, no subscription.',
            },
            { id: 'paidTitle', kind: 'text', label: 'Paid row heading', default: 'Paid courses' },
            {
              id: 'paidBody',
              kind: 'text',
              label: 'Paid row sub-heading',
              default: 'Buy once, keep forever.',
            },
            {
              id: 'subscriberTitle',
              kind: 'text',
              label: 'Subscription row heading',
              default: 'Subscription courses',
            },
            {
              id: 'subscriberBody',
              kind: 'text',
              label: 'Subscription row sub-heading',
              default: 'Included while your subscription is active.',
            },
            { id: 'seeAllLabel', kind: 'text', label: '"See all" link', default: 'See all' },
          ],
        },
        {
          id: 'mentors',
          label: 'Mentors',
          hint: 'Approved instructors who have at least one published course, most students first.',
          fields: [
            {
              id: 'show',
              kind: 'select',
              label: 'Show this block',
              options: SHOW_HIDE,
              default: 'show',
            },
            { id: 'heading', kind: 'text', label: 'Heading', default: 'Learn from real sellers' },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Sub-heading',
              default: "Mentors who built their businesses on the same platform you're on.",
            },
          ],
        },
        {
          id: 'earn',
          label: 'Share & earn band',
          hint: 'Only ever shown while "Share & earn" is switched on (Learning → Learning Settings).',
          fields: [
            { id: 'title', kind: 'text', label: 'Title', default: 'Love a course? Share it.' },
            {
              id: 'body',
              kind: 'textarea',
              label: 'Body',
              default:
                'Every member gets a share link. When a friend enrolls through it, you earn a reward — no selling experience needed.',
            },
            { id: 'ctaLabel', kind: 'text', label: 'Button', default: 'See how it works' },
            {
              id: 'ctaHref',
              kind: 'text',
              label: 'Button link',
              default: '/become-member',
              hint: 'A path on this site (starts with /) or a full https:// address.',
            },
          ],
        },
      ],
    },
  ],
};
